import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useMemo } from 'react';
import type {
  SurveyResponseAnswer,
  SurveyResponseDto as Response,
} from '../../../../../domain/Surveys/Responses/SurveyResponseDto';
import type { SurveyQuestion } from '../../../../../domain/Surveys/Questions';
import { QuestionTypes } from '../../../../../domain/Surveys/Questions';
import type {
  ReactionScore,
  ReactionStyle,
} from '../../../../../domain/Surveys/Questions/SurveyQuestion';
import { ScoreReactionIcon } from '../../../../ReactionIcon/ScoreReactionIcon';
import { TextWithLinks } from '../../../../common/TextWithLinks/TextWithLinks';

const AnswerContainer = styled.div`
  min-width: 300px;
  display: flex;
  flex-direction: column;
  grid-gap: ${tokens.spacer.x1};
`;

const AnswerList = styled.ol`
  list-style-type: lower-roman;
  padding-left: 1em;
`;

const Comment = styled.span`
  color: ${tokens.color.text_light};
`;

const AnswerText = ({ answer }: { answer: SurveyResponseAnswer }) => {
  return (
    <AnswerContainer>
      <span>
        <TextWithLinks>{answer.answer}</TextWithLinks>
      </span>
      {answer.comment !== undefined && (
        <Comment>
          <TextWithLinks>{answer.comment}</TextWithLinks>
        </Comment>
      )}
    </AnswerContainer>
  );
};

const Reaction = ({ answer, style }: { answer: SurveyResponseAnswer; style: ReactionStyle }) => {
  return <ScoreReactionIcon score={answer.answer as ReactionScore} reactionStyle={style} />;
};

const Answer = ({
  answer,
  question,
}: {
  answer: SurveyResponseAnswer;
  question: SurveyQuestion;
}) => {
  const questionType = question.type;

  switch (questionType) {
    case QuestionTypes.REACTION:
      return <Reaction answer={answer} style={question.reaction_style} />;
    default:
      return <AnswerText answer={answer} />;
  }
};

export const Answers = ({
  response,
  question,
}: {
  response: Response;
  question: SurveyQuestion;
}): JSX.Element | null => {
  const answers = useMemo(
    () =>
      response.content.answers.filter(
        (answer) => answer.questionUuid === question.uuid.substring(0, 8),
      ),
    [response, question],
  );

  if (answers.length === 0) return null;

  if (answers.length === 1) return <Answer answer={answers[0]} question={question} />;

  return (
    <AnswerList>
      {answers.map((answer, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index}>
          <Answer answer={answer} question={question} />
        </li>
      ))}
    </AnswerList>
  );
};
